/**
 * OnboardingPage — "Laboratoires": the cross-lab laboratory DIRECTORY + the provision-a-new-lab form +
 * the lab lifecycle actions (actif ⇄ suspendu). The platform super-admin's lab inventory.
 *
 *   • TABLE      — GET /management/labs (server-paginated + filtered by statut / deploy class / substring).
 *   • PROVISION  — POST /management/labs (a new lab + its first owner, atomic) via <ProvisionLabModal>.
 *   • LIFECYCLE  — POST /management/labs/:id/statut (suspend ⇄ reactivate) via <LabLifecycleModal>.
 *
 * GATING — this whole console is super-admin-only (the route rides <PlatformAdminGuard> in index.tsx);
 * this screen ALSO renders its own <ForbiddenState> defensively and short-circuits the offline + the
 * "module not mounted on this install" (cloud-only) states. Full 5-state DoD on the directory read. Writes
 * are the server's trust boundary (the screen only hides/disables UI). Token-driven, AA-contrast, RTL-safe.
 *
 * Registers its own i18n at module-load (registerOnboardingMessages) — no shell hookup needed.
 */
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Pagination,
  Select,
  Table,
  type Column,
} from "@/ui";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent, useLabs } from "../api";
import { useIsPlatformAdmin } from "../session";
import { formatDate, labStatutTone } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { DistributionBar, StatChips, type DistributionSlice } from "../components/Distribution";
import { ProvisionLabModal } from "../components/ProvisionLabModal";
import {
  LabLifecycleModal,
  type LabLifecycleAction,
} from "../components/LabLifecycleModal";
import type { LabDirectoryFilters, LabDirectoryRow, LabLifecycleStatut } from "../types";
import { registerOnboardingMessages } from "./onboarding.messages";

registerOnboardingMessages();

const PAGE_SIZE = 20;
const DEPLOY_CLASSES = ["local", "cloud", "hybrid"] as const;
const STATUT_OPTIONS: ReadonlyArray<LabLifecycleStatut> = ["actif", "suspendu"];

/** A stable, accessible tone per deploy class for the insight meter (paired with text — never color-only). */
const DEPLOY_TONE: Record<string, DistributionSlice["tone"]> = {
  cloud: "info",
  local: "success",
  hybrid: "warn",
};

export function OnboardingPage() {
  const { t, locale } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();

  // Server-side filters (the directory is server-paginated; the search debounces via the controlled input).
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<LabLifecycleStatut | "">("");
  const [deployClass, setDeployClass] = useState<string>("");
  const [wilaya, setWilaya] = useState("");
  const [page, setPage] = useState(1);

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [lifecycle, setLifecycle] = useState<{ lab: LabDirectoryRow; action: LabLifecycleAction } | null>(
    null,
  );

  const filters: LabDirectoryFilters = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(statut ? { statut } : {}),
      ...(deployClass ? { deploy_class: deployClass } : {}),
      ...(wilaya.trim() ? { wilaya: wilaya.trim() } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [search, statut, deployClass, wilaya, page],
  );

  const query = useLabs(filters, isAdmin && online);
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  const totalPages = query.data?.totalPages ?? 1;
  const total = query.data?.total ?? 0;
  const hasFilters = !!(search.trim() || statut || deployClass || wilaya.trim());

  // Per-page insight (DERIVED from the loaded rows — schema-free, no extra read): the deploy-class mix +
  // the active/suspended split for the current directory page. Refreshes in lock-step with the table.
  const deploySlices = useMemo<DistributionSlice[]>(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const k = r.deployClass ?? "—";
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ key: k, label: k, value: v, tone: DEPLOY_TONE[k] ?? "neutral" }));
  }, [rows]);
  const activeCount = useMemo(() => rows.filter((r) => r.statut === "actif").length, [rows]);
  const suspendedCount = rows.length - activeCount;

  const columns: ReadonlyArray<Column<LabDirectoryRow>> = [
    {
      key: "reference",
      header: t("mgmt.onboarding.col.reference"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => (r.reference ?? "").toLowerCase(),
      cell: (r) => <span className="nv-ltr-island">{r.reference ?? "—"}</span>,
    },
    {
      key: "name",
      header: t("mgmt.onboarding.col.name"),
      sortable: true,
      sortAccessor: (r) => (r.raisonSociale ?? r.nomCommercial ?? "").toLowerCase(),
      cell: (r) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{r.raisonSociale ?? "—"}</span>
          {r.nomCommercial && r.nomCommercial !== r.raisonSociale && (
            <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
              {r.nomCommercial}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "deploy",
      header: t("mgmt.onboarding.col.deploy"),
      sortable: true,
      sortAccessor: (r) => r.deployClass ?? "",
      cell: (r) =>
        r.deployClass ? (
          <Badge tone={DEPLOY_TONE[r.deployClass] ?? "info"} glyph="◷">
            {r.deployClass}
          </Badge>
        ) : (
          "—"
        ),
    },
    {
      key: "location",
      header: t("mgmt.onboarding.col.location"),
      sortable: true,
      sortAccessor: (r) => [r.wilaya, r.commune].filter(Boolean).join(" ").toLowerCase(),
      cell: (r) => [r.commune, r.wilaya].filter(Boolean).join(", ") || "—",
    },
    {
      key: "contact",
      header: t("mgmt.onboarding.col.contact"),
      cell: (r) =>
        r.telephone || r.email ? (
          <span className="nv-ltr-island">{r.telephone ?? r.email}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "statut",
      header: t("mgmt.onboarding.col.statut"),
      sortable: true,
      sortAccessor: (r) => r.statut,
      cell: (r) => (
        <Badge tone={labStatutTone(r.statut)} glyph={r.statut === "actif" ? "●" : "❙❙"}>
          {t(`mgmt.onboarding.statut.${r.statut}`)}
        </Badge>
      ),
    },
    {
      key: "created",
      header: t("mgmt.onboarding.col.created"),
      sortable: true,
      sortAccessor: (r) => r.createdAt ?? "",
      cell: (r) => <span className="nv-ltr-island">{formatDate(r.createdAt, locale)}</span>,
    },
    {
      key: "actions",
      header: t("mgmt.onboarding.col.actions"),
      align: "end",
      cell: (r) => (
        <div style={{ display: "inline-flex", gap: "var(--nv-s-1)", justifyContent: "flex-end" }}>
          {r.statut === "actif" ? (
            <Button size="sm" variant="ghost" onClick={() => setLifecycle({ lab: r, action: "suspend" })}>
              {t("mgmt.onboarding.action.suspend")}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setLifecycle({ lab: r, action: "reactivate" })}
            >
              {t("mgmt.onboarding.action.reactivate")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const header = (
    <PageHeader
      title={t("mgmt.onboarding.title")}
      subtitle={t("mgmt.onboarding.subtitle")}
      actions={<Button onClick={() => setProvisionOpen(true)}>{t("mgmt.onboarding.new")}</Button>}
    />
  );

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />
      </Screen>
    );
  }

  // Cloud-only module on a local box → its routes 404. Surface the clean "not available here" notice.
  if (isManagementAbsent(query.error)) {
    return (
      <Screen>
        {header}
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.unavailable.desc")}</p>
        </Card>
      </Screen>
    );
  }

  const onFilterChange = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1); // any filter change resets to the first server page
  };

  return (
    <Screen>
      {header}

      {!query.isLoading && !query.isError && rows.length > 0 && (
        <Card title={t("mgmt.onboarding.insight.title")}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <StatChips
              chips={[
                { key: "total", label: t("mgmt.onboarding.insight.total"), value: rows.length },
                {
                  key: "active",
                  label: t("mgmt.onboarding.insight.active"),
                  value: activeCount,
                  tone: "success",
                },
                {
                  key: "suspended",
                  label: t("mgmt.onboarding.insight.suspended"),
                  value: suspendedCount,
                  tone: suspendedCount > 0 ? "danger" : "neutral",
                },
              ]}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
              <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-text-2, var(--nv-slate))" }}>
                {t("mgmt.onboarding.insight.deploy")}
              </span>
              <DistributionBar
                slices={deploySlices}
                emptyLabel={t("mgmt.onboarding.insight.empty")}
                ariaLabel={t("mgmt.onboarding.insight.deploy.aria")}
              />
            </div>
          </div>
        </Card>
      )}

      <Card padded={false}>
        <div
          style={{
            display: "flex",
            gap: "var(--nv-s-3)",
            flexWrap: "wrap",
            padding: "var(--nv-s-3)",
            borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
          }}
        >
          <div style={{ flex: "1 1 16rem", minWidth: "12rem" }}>
            <Input
              value={search}
              onChange={(e) => onFilterChange(setSearch)(e.target.value)}
              placeholder={t("mgmt.onboarding.search")}
              aria-label={t("mgmt.onboarding.search")}
            />
          </div>
          <Select
            value={statut}
            onChange={(e) => onFilterChange(setStatut)(e.target.value as LabLifecycleStatut | "")}
            aria-label={t("mgmt.onboarding.filter.statut")}
          >
            <option value="">{`${t("mgmt.onboarding.filter.statut")} — ${t("mgmt.onboarding.filter.all")}`}</option>
            {STATUT_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`mgmt.onboarding.statut.${s}`)}
              </option>
            ))}
          </Select>
          <Select
            value={deployClass}
            onChange={(e) => onFilterChange(setDeployClass)(e.target.value)}
            aria-label={t("mgmt.onboarding.filter.deploy")}
          >
            <option value="">{`${t("mgmt.onboarding.filter.deploy")} — ${t("mgmt.onboarding.filter.all")}`}</option>
            {DEPLOY_CLASSES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
          <div style={{ flex: "0 1 12rem", minWidth: "9rem" }}>
            <Input
              value={wilaya}
              onChange={(e) => onFilterChange(setWilaya)(e.target.value)}
              placeholder={t("mgmt.onboarding.filter.wilaya")}
              aria-label={t("mgmt.onboarding.filter.wilaya")}
              className="nv-ltr-island"
            />
          </div>
        </div>

        <div style={{ padding: "var(--nv-s-3)" }}>
          {query.isLoading ? (
            <LoadingState variant="table" rows={6} />
          ) : query.isError ? (
            <ErrorState
              message={query.error.message}
              code={query.error.code}
              onRetry={() => void query.refetch()}
            />
          ) : (
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              caption={t("mgmt.onboarding.title")}
              emptyTitle={hasFilters ? t("mgmt.onboarding.empty.filtered") : t("mgmt.onboarding.empty.title")}
              {...(hasFilters ? {} : { emptyDescription: t("mgmt.onboarding.empty.desc") })}
              emptyAction={
                !hasFilters ? (
                  <Button onClick={() => setProvisionOpen(true)}>{t("mgmt.onboarding.new")}</Button>
                ) : undefined
              }
            />
          )}
        </div>

        {!query.isLoading && !query.isError && totalPages > 1 && (
          <div style={{ padding: "var(--nv-s-3)", borderTop: "1px solid var(--nv-border-subtle, var(--nv-line-faint))" }}>
            <Pagination page={page} pageCount={totalPages} total={total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <ProvisionLabModal open={provisionOpen} onClose={() => setProvisionOpen(false)} />

      <LabLifecycleModal
        lab={lifecycle?.lab ?? null}
        action={lifecycle?.action ?? null}
        onClose={() => setLifecycle(null)}
      />
    </Screen>
  );
}
